import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { Button, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import { loginUser } from "../../api/Api";
import GlowCard from '../../shared/GlowCard';
import { useColors } from '../../theme/ColorModeContext';
import AuthBrandHeader from '../../shared/AuthBrandHeader';

interface LoginProps {
    setToken: React.Dispatch<React.SetStateAction<any>>;
}

const Login = (loginProps: LoginProps) => {
    const colors = useColors();
    const setToken = loginProps.setToken;
    const [message, setMessage] = useState("");
    const navigate = useNavigate();

    const handleSubmit = async(e: any) => {
        e.preventDefault();
        const data = new FormData(e.currentTarget);
        const email = `${data.get("email")}`;
        const password = `${data.get("password")}`;
        const response = await loginUser({ email, password });
        if(response) {
            if(response.status === 200) {
                setToken(response.data.access_token);
                navigate("/");
            } else {
                setMessage(response.data.msg);
            }
        }
    }

    return (
        <Container sx={{ marginTop: '120px' }} disableGutters maxWidth="sm" className="page-enter">
            <AuthBrandHeader />
            <GlowCard gradientBorder sx={{ padding: '48px 32px' }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                    <Typography variant="h4" sx={{
                        fontFamily: '"Orbitron", sans-serif',
                        fontWeight: 700,
                        background: colors.gradient.primary,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        marginBottom: '32px',
                        // Isolates this element in its own compositing layer so the gradient
                        // text clip survives the ancestor Container's page-enter animation
                        // (Chrome/WebKit can drop the clip repaint once that layer decomposites).
                        transform: 'translateZ(0)',
                        willChange: 'transform',
                    }}>
                        Sign In
                    </Typography>
                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email"
                            name="email"
                            autoFocus
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            id="password"
                            label="Password"
                            type="password"
                        />
                        <Box sx={{ textAlign: 'right', marginTop: '4px' }}>
                            <Link to={"/forgot-password"} style={{ textDecoration: 'none' }}>
                                <Typography variant="body2" sx={{
                                    color: colors.text.secondary,
                                    '&:hover': { color: colors.accent.cyan },
                                    transition: 'color 0.2s ease',
                                }}>
                                    Forgot your password?
                                </Typography>
                            </Link>
                        </Box>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 3, padding: '12px' }}
                        >
                            Sign In
                        </Button>
                        <Grid container alignItems="center">
                            <Grid item xs={6}>
                                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                                    Don't have an account?
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Link to={"/register"} style={{ textDecoration: 'none' }}>
                                    <Button fullWidth variant="outlined">
                                        Register
                                    </Button>
                                </Link>
                            </Grid>
                        </Grid>
                        {message && (
                            <Typography variant="body2" sx={{
                                color: colors.accent.red,
                                marginTop: '16px',
                                padding: '8px 12px',
                                backgroundColor: colors.accent.redGlow,
                                borderRadius: '8px',
                                textAlign: 'center',
                            }}>
                                {message}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </GlowCard>
        </Container>
    );
}

export default Login;
